import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import VideoModal from '../ui/VideoModal'
import { posterUrl } from '../ui/videoSource'
import CoachesVideosForm from './CoachesVideosForm'
import {
  Notice,
  EmptyState,
  IconPencil,
  IconPlay,
  IconPlus,
} from './media'
import './media.css'
import './coaches.css'

/*
 * COACHES' VIDEOS — the gallery.
 *
 * The client could not tell what a video WAS without opening its row, so the
 * table is gone. What replaces it is the thing itself: a grid of poster frames.
 *
 * Two controls per card, never the same one:
 *   - the poster is a button whose only job is PLAY (shared VideoModal, and it
 *     walks prev/next through the rest of the filtered grid)
 *   - a separate labelled "Επεξεργασία" button in the card's own footer bar,
 *     outside the poster's hit area, opens the form
 * The <article> itself carries no click handler, so there is no ambient target
 * that could mean either one. Nothing on the card needs :hover to be found.
 *
 * Requests are exactly the ones this screen always made:
 *   GET admin/coaches   GET admin/sports
 * plus two that already belong to the module and are reused read-only to answer
 * "which languages does this post exist in?":
 *   GET admin/lang
 *   GET admin/coaches/videolang/{CoachVideoID}/{LangID}
 * The second is per card and per language, so it is only fired for cards that
 * actually reach the viewport, at most four at a time, once each. Nothing is
 * written from this screen.
 */

/* A challenge that ran out of time reads as inactive — unchanged rule. */
const activeOf = (row) => {
  const yourDate = new Date()
  const today = yourDate.toISOString().split('T')[0]
  return row.IsChallenge != 1 ? row.IsActive : row.DateTo < today ? 0 : row.IsActive
}

const isOn = (row) => Number(activeOf(row)) === 1

const shortDate = (value) => String(value || '').split('T')[0].slice(0, 10)

const haystack = (row) => [
  row.Title,
  row.Tags,
  row.Level,
  row.SportName,
  row.VideoID,
].map((v) => String(v == null ? '' : v)).join(' ').toUpperCase()

const SORTS = [
  { value: 'new', label: 'Νεότερα πρώτα' },
  { value: 'old', label: 'Παλαιότερα πρώτα' },
  { value: 'az', label: 'Τίτλος Α → Ω' },
  { value: 'za', label: 'Τίτλος Ω → Α' },
]

const STATES = [
  { value: '', label: 'Όλα' },
  { value: 'on', label: 'Ενεργά' },
  { value: 'off', label: 'Ανενεργά' },
]

/* ------------------------------------------------------------------ *
 * ONE CARD
 * The poster plays. The footer button edits. They are different elements,
 * different labels and different tab stops.
 * ------------------------------------------------------------------ */
function CoachCard({ row, langs, coverage, epoch, onPlay, onEdit, onNeedLangs }) {

  const [artBroken, setArtBroken] = useState(false)
  const hostRef = useRef(null)

  const uuid = row.UUID
  const hasClip = !!(row.VideoID && String(row.VideoID).length > 0)
  const poster = useMemo(() => (hasClip ? posterUrl(row.VideoID) : ''), [hasClip, row.VideoID])
  const showArt = !!poster && !artBroken
  const title = row.Title || 'Χωρίς τίτλο'

  /* Language coverage costs one request per language, so it is only asked for
     once the card is actually near the screen. `epoch` re-arms the observer —
     it changes when the language list finally lands and when the gallery is
     reloaded after a save, which is exactly when the answer can be stale. */
  useEffect(() => {
    const el = hostRef.current
    if (!el || !uuid) return undefined

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      onNeedLangs(uuid)
      return undefined
    }

    const io = new window.IntersectionObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        if (entries[i].isIntersecting) {
          onNeedLangs(uuid)
          io.disconnect()
          return
        }
      }
    }, { rootMargin: '250px 0px' })

    io.observe(el)
    return () => io.disconnect()
  }, [uuid, epoch, onNeedLangs])

  const known = coverage || null

  return (
    <article className="cv-card" ref={hostRef}>

      <div className="cv-art">
        {hasClip ? (
          <button
            type="button"
            className="cv-play"
            onClick={() => onPlay(row)}
            aria-label={'Αναπαραγωγή — ' + title}
          >
            {showArt ? (
              <img
                className="cv-art-img"
                src={poster}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setArtBroken(true)}
              />
            ) : (
              <span className="cv-art-blank" aria-hidden="true" />
            )}
            <span className="cv-playmark" aria-hidden="true">{IconPlay}</span>
          </button>
        ) : (
          <div className="cv-play cv-play-empty">
            <span className="cv-art-blank" aria-hidden="true" />
            <span className="cv-noclip">Χωρίς βίντεο</span>
          </div>
        )}

        <span className={isOn(row) ? 'cv-state cv-state-on' : 'cv-state cv-state-off'}>
          {isOn(row) ? 'Ενεργό' : 'Ανενεργό'}
        </span>

        {row.Level ? <span className="cv-level">{row.Level}</span> : null}
      </div>

      <div className="cv-body">
        <h3 className="cv-title">{title}</h3>

        <div className="cv-meta">
          <span className="cv-sport">{row.SportName || '—'}</span>
          <span className="cv-meta-dot" aria-hidden="true" />
          <span className="cv-date mono">{shortDate(row.DateCreated)}</span>
        </div>

        <div className="cv-langs">
          <span className="cv-langs-lbl">Γλώσσες</span>
          {langs.length === 0 ? (
            <span className="cv-lang cv-lang-wait">—</span>
          ) : (
            langs.map((lang) => {
              const state = known ? (known[lang.UUID] ? 'on' : 'off') : 'wait'
              return (
                <span
                  key={lang.LangID}
                  className={'cv-lang cv-lang-' + state}
                  title={
                    state === 'on'
                      ? lang.LangName + ' — υπάρχει μετάφραση'
                      : state === 'off'
                        ? lang.LangName + ' — λείπει μετάφραση'
                        : lang.LangName
                  }
                >
                  {lang.LangName}
                </span>
              )
            })
          )}
        </div>

        {row.Tags ? <p className="cv-tags">{row.Tags}</p> : null}
      </div>

      {/* Editing lives here and nowhere else on the card: its own strip,
          below the text, outside the poster's hit area, and labelled. */}
      <div className="cv-foot">
        <Btn
          className="cv-edit"
          onClick={() => onEdit(row)}
          aria-label={'Επεξεργασία στοιχείων — ' + title}
        >
          {IconPencil}
          Επεξεργασία
        </Btn>
      </div>
    </article>
  )
}

/* ------------------------------------------------------------------ *
 * THE SCREEN
 * ------------------------------------------------------------------ */
export default function CoachesVideos() {

  // Local, not context: a shared form flag leaked between screens.
  const [form, setForm] = useState(false)
  const [id, setId] = useState(0)

  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState('')
  const [data, setData] = useState([])
  const [sports, setSports] = useState([])
  const [langs, setLangs] = useState([])

  const [query, setQuery] = useState('')
  const [sportFlt, setSportFlt] = useState('')
  const [levelFlt, setLevelFlt] = useState('')
  const [stateFlt, setStateFlt] = useState('')
  const [sort, setSort] = useState('new')

  const [playIndex, setPlayIndex] = useState(-1)

  /* Language coverage: { [videoUUID]: { [langUUID]: bool } } */
  const [cover, setCover] = useState({})
  /* Bumped whenever coverage must be asked for again. */
  const [epoch, setEpoch] = useState(0)

  const aliveRef = useRef(true)
  const langsRef = useRef([])
  const askedRef = useRef(new Set())
  const queueRef = useRef([])
  const activeRef = useRef(0)

  useEffect(() => () => { aliveRef.current = false }, [])

  useEffect(() => {
    loadData()
    loadSports()
    loadLangs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  /* ---------------- requests (unchanged URLs, unchanged handling) --------- */

  const loadData = () => {
    setIsLoading(true)
    setFailed('')
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/coaches', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data.results)
        setIsLoading(false)
        askedRef.current = new Set()
        queueRef.current = []
        setCover({})
        setEpoch((n) => n + 1)

      }).catch(err => {
        setIsLoading(false)
        setFailed('Δεν ήταν δυνατή η φόρτωση των βίντεο. Δοκιμάστε ξανά.')
      })
  }

  const loadSports = () => {
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sports', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSports(res.data.results)

      }).catch(err => {
        setFailed('Δεν ήταν δυνατή η φόρτωση των αθλημάτων.')
      })
  }

  /* The same list the language tabs inside the form read. The join in the API
     can hand back the same language more than once, so it is keyed down to one
     row per LangID before anything renders it. */
  const loadLangs = () => {
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/lang', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        const rows = Array.isArray(res.data.results) ? res.data.results : []
        const seen = {}
        const unique = []
        rows.forEach((item) => {
          if (!item || item.LangID == null) return
          if (seen[item.LangID]) return
          seen[item.LangID] = true
          unique.push(item)
        })
        langsRef.current = unique
        setLangs(unique)
        setEpoch((n) => n + 1)

      }).catch(err => {
        langsRef.current = []
        setLangs([])
      })
  }

  /* ---------------- language coverage, throttled and lazy ---------------- */

  const pump = useCallback(() => {
    while (activeRef.current < 4 && queueRef.current.length > 0) {
      const job = queueRef.current.shift()
      activeRef.current += 1
      job().then(() => {
        activeRef.current -= 1
        pump()
      })
    }
  }, [])

  const markCover = useCallback((videoUUID, langUUID, has) => {
    if (!aliveRef.current) return
    setCover((prev) => {
      const row = prev[videoUUID] || {}
      return { ...prev, [videoUUID]: { ...row, [langUUID]: has } }
    })
  }, [])

  const requestCover = useCallback((videoUUID) => {
    if (!videoUUID) return
    const list = langsRef.current
    if (!list || list.length === 0) return

    list.forEach((lang) => {
      const key = videoUUID + '|' + lang.UUID
      if (askedRef.current.has(key)) return
      askedRef.current.add(key)

      queueRef.current.push(() =>
        axios.get(process.env.REACT_APP_API_SERVER + 'admin/coaches/videolang/' + videoUUID + '/' + lang.UUID, {
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
          }
        })
          .then(res => {
            const row = res.data && res.data.results
            const has = !!(row && row.id != 0 && String(row.Title == null ? '' : row.Title).trim().length > 0)
            markCover(videoUUID, lang.UUID, has)

          }).catch(err => {
            markCover(videoUUID, lang.UUID, false)
          })
      )
    })

    pump()
  }, [markCover, pump])

  /* ---------------- filtering ---------------- */

  const levels = useMemo(() => {
    const seen = {}
    const out = []
    ;(Array.isArray(data) ? data : []).forEach((row) => {
      const value = String(row.Level == null ? '' : row.Level).trim()
      if (!value || seen[value]) return
      seen[value] = true
      out.push(value)
    })
    return out.sort()
  }, [data])

  const shown = useMemo(() => {
    let rows = Array.isArray(data) ? data.slice() : []

    const s = query.trim().toUpperCase()
    if (s.length > 0) rows = rows.filter((row) => haystack(row).indexOf(s) > -1)

    if (sportFlt.length > 0) rows = rows.filter((row) => String(row.SportID) === String(sportFlt))
    if (levelFlt.length > 0) rows = rows.filter((row) => String(row.Level || '') === levelFlt)
    if (stateFlt === 'on') rows = rows.filter((row) => isOn(row))
    if (stateFlt === 'off') rows = rows.filter((row) => !isOn(row))

    if (sort === 'az' || sort === 'za') {
      rows.sort((a, b) => String(a.Title || '').localeCompare(String(b.Title || ''), 'el'))
      if (sort === 'za') rows.reverse()
    } else if (sort === 'old') {
      rows.sort((a, b) => String(a.DateCreated || '').localeCompare(String(b.DateCreated || '')))
    } else {
      rows.sort((a, b) => String(b.DateCreated || '').localeCompare(String(a.DateCreated || '')))
    }

    return rows
  }, [data, query, sportFlt, levelFlt, stateFlt, sort])

  /* The modal walks the grid the user is actually looking at. */
  const clips = useMemo(
    () => shown
      .filter((row) => row.VideoID && String(row.VideoID).length > 0)
      .map((row) => ({
        fileId: row.VideoID,
        uuid: row.UUID,
        name: row.Title || row.VideoID,
        subtitle: row.SportName || row.VideoID,
      })),
    [shown]
  )

  useEffect(() => {
    if (playIndex >= clips.length) setPlayIndex(-1)
  }, [clips.length, playIndex])

  const activeCount =
    (query.trim().length > 0 ? 1 : 0) +
    (sportFlt.length > 0 ? 1 : 0) +
    (levelFlt.length > 0 ? 1 : 0) +
    (stateFlt.length > 0 ? 1 : 0)

  const clearCretiria = () => {
    setQuery('')
    setSportFlt('')
    setLevelFlt('')
    setStateFlt('')
  }

  const showForm = (nextId, a) => {
    setForm(a)
    setId(nextId)
  }

  const play = (row) => {
    const at = clips.findIndex((clip) => clip.uuid === row.UUID)
    if (at > -1) setPlayIndex(at)
  }

  const edit = (row) => showForm(row.UUID, true)

  const total = Array.isArray(data) ? data.length : 0

  const sportsList = (Array.isArray(sports) ? sports : []).map(item => {
    return (
      <option key={item.SportID} value={item.SportID}>{item.SportName}</option>
    )
  })

  /* The form owns its own panels — the gallery steps aside entirely. */
  if (form) {
    return <CoachesVideosForm id={id} showForm={showForm} setForm={setForm} loadData={loadData} />
  }

  return (
    <div className="md-stack">
      <Panel
        title="Coaches Videos"
        subtitle={
          isLoading
            ? 'Φόρτωση...'
            : activeCount > 0
              ? shown.length + ' από ' + total + ' βίντεο'
              : shown.length + ' βίντεο'
        }
        actions={
          <Btn variant="accent" onClick={() => { showForm(0, true) }}>
            {IconPlus}
            Προσθήκη
          </Btn>
        }
      >
        {failed ? <Notice kind="crit" onClose={() => setFailed('')}>{failed}</Notice> : null}

        <div className="cv-bar">
          <div className="cv-bar-find">
            <label className="cv-lbl" htmlFor="cv-q">Αναζήτηση</label>
            <input
              id="cv-q"
              className="md-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Τίτλος, tag, level ή VideoID..."
            />
          </div>

          <div className="cv-bar-pick">
            <label className="cv-lbl" htmlFor="cv-sport-flt">Άθλημα</label>
            <select
              id="cv-sport-flt"
              className="md-select"
              value={sportFlt}
              onChange={(e) => setSportFlt(e.target.value)}
            >
              <option value=''>Όλα τα αθλήματα</option>
              {sportsList}
            </select>
          </div>

          <div className="cv-bar-pick">
            <label className="cv-lbl" htmlFor="cv-level-flt">Level</label>
            <select
              id="cv-level-flt"
              className="md-select"
              value={levelFlt}
              onChange={(e) => setLevelFlt(e.target.value)}
            >
              <option value=''>Όλα τα levels</option>
              {levels.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>

          <div className="cv-bar-pick">
            <label className="cv-lbl" htmlFor="cv-sort">Ταξινόμηση</label>
            <select
              id="cv-sort"
              className="md-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              {SORTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>

          <div className="cv-bar-state">
            <span className="cv-lbl" id="cv-state-lbl">Κατάσταση</span>
            <div className="cv-seg" role="group" aria-labelledby="cv-state-lbl">
              {STATES.map((item) => (
                <button
                  key={item.value || 'all'}
                  type="button"
                  className={stateFlt === item.value ? 'cv-seg-btn cv-seg-on' : 'cv-seg-btn'}
                  aria-pressed={stateFlt === item.value}
                  onClick={() => setStateFlt(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cv-bar-clear">
            <Btn onClick={clearCretiria} disabled={activeCount === 0}>Καθαρισμός</Btn>
          </div>
        </div>

        {isLoading ? (
          <div className="cv-grid" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
              <div className="cv-card cv-card-skel" key={n}>
                <div className="skeleton cv-skel-art" />
                <div className="cv-body">
                  <div className="skeleton cv-skel-line cv-skel-line-lg" />
                  <div className="skeleton cv-skel-line" />
                  <div className="skeleton cv-skel-line cv-skel-line-sm" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {!isLoading && shown.length === 0 ? (
          <EmptyState
            title="Κανένα βίντεο"
            hint={activeCount > 0 ? 'Κανένα αποτέλεσμα με αυτά τα κριτήρια.' : 'Προσθέστε το πρώτο βίντεο.'}
            action={
              activeCount > 0
                ? <Btn onClick={clearCretiria}>Καθαρισμός φίλτρων</Btn>
                : <Btn variant="accent" onClick={() => { showForm(0, true) }}>{IconPlus}Προσθήκη</Btn>
            }
          />
        ) : null}

        {!isLoading && shown.length > 0 ? (
          <div className="cv-grid">
            {shown.map((row) => (
              <CoachCard
                key={row.UUID}
                row={row}
                langs={langs}
                coverage={cover[row.UUID]}
                epoch={epoch}
                onPlay={play}
                onEdit={edit}
                onNeedLangs={requestCover}
              />
            ))}
          </div>
        ) : null}
      </Panel>

      {playIndex >= 0 && clips[playIndex] ? (
        <VideoModal
          items={clips}
          index={playIndex}
          title="Coaches Videos"
          onIndex={setPlayIndex}
          onClose={() => setPlayIndex(-1)}
        />
      ) : null}
    </div>
  )
}
